import { FINNISH_LOCALITY_LOCALE_NAMES } from '../geocoding/finnish-locality-locale-names';
import {
  isRemotePathMarker,
  remoteHubScopeToLocationFacet,
  remoteScopeSlugToEnglishScope,
  type RemoteHubEnglishScope,
} from '../location/remote-hub-scope';
import { toLocationSlug } from '../location/to-location-slug';
import { workModelForSegment, workModelSegment, type WorkModelKey } from '../work-model/work-model-hub';
import { encodeLocationUrlPathSegment } from '../location/encode-location-url-path-segment';
import { getDevSiteOrigin, FALLBACK_DEV_SITE_ORIGIN } from './dev-site-origin';
import { localizedSummerJobsArticlePath } from '../routing/summer-jobs-resource-paths';
import { VITE_DEV } from '../env/vite-env';

export const LOCALIZED_PATHS = {
  en: {
    job: 'job',
    jobs: 'jobs',
    /** Roles index segment under the jobs segment: /jobs/roles, /tyopaikat/ammatit, /lediga-jobb/yrken. */
    rolesIndex: 'roles',
    search: 'search',
    blog: 'resources',
    profile: 'profile',
    savedJobs: 'tools/saved-jobs',
    savedSearches: 'tools/saved-searches',
    appliedJobs: 'tools/applied-jobs',
    viewedJobs: 'tools/viewed-jobs',
    jobFlowSankey: 'tools/job-flow-sankey',
    jobAlertsUnsubscribe: 'tools/job-alerts/unsubscribe',
    jobAlertsConfirm: 'tools/job-alerts/confirm',
    searchesAndAlerts: 'tools/searches',
    watchlist: 'tools/watchlist',
    partnerships: 'partnerships',
    privacy: 'privacy',
    terms: 'terms',
    features: 'features',
    forEmployers: 'for-employers',
    locations: 'locations',
    summerJobs: 'resources',
    studentsAndGraduates: 'resources',
    // Employers index (was the "best companies" ranking hub; retired into a directory of employers
    // hiring now, 2026-09-04). The logical Vike route stays /best-companies. sv cannot reuse the
    // bare `arbetsgivare` segment: its city pages would collide with `/sv/arbetsgivare/{slug}`.
    bestCompanies: 'employers',
    // A to Z employer directory under the employers index: /employers/a-z[/{letter}].
    employersAz: 'a-z',
    // Employer profile segment. "employer"/"tyonantaja" are the words searchers use ("{brand}
    // työpaikat", "parhaat työnantajat"); the previous "company"/"yritys" segments still route and
    // 301 to the current form (COMPANY_PROFILE_LEGACY_SEGMENTS).
    company: 'employer',
    // Hosted employer careers pages (TEC-488): /careers/:companyslug, white-label, no site chrome.
    careers: 'careers',
    freeCareersPage: 'free-careers-page',
    salary: 'salary',
    about: 'about',
    editorialStandards: 'editorial-standards',
    contact: 'contact',
    sitemap: 'sitemap',
    accessibilityStatement: 'accessibility-statement',
    accountDeletion: 'account-deletion',
    aboutTeam: 'about/team',
    recruiterContactInfo: 'recruiter-contact-info',
    // Collective agreements (TES) directory + per-agreement pages: /collective-agreements[/{slug}].
    collectiveAgreements: 'collective-agreements',
  },
  fi: {
    job: 'tyopaikka', // työpaikka without special characters
    jobs: 'tyopaikat', // työpaikat without special characters
    rolesIndex: 'ammatit',
    search: 'haku',
    blog: 'tyoelama',
    profile: 'profiili',
    savedJobs: 'tyokalut/tallennetut-tyopaikat',
    savedSearches: 'tyokalut/tallennetut-haut',
    appliedJobs: 'tyokalut/haetut-tyopaikat',
    viewedJobs: 'tyokalut/katsotut-tyopaikat',
    jobFlowSankey: 'tyokalut/tyovirta-sankey',
    jobAlertsUnsubscribe: 'tyokalut/tyopaikkavahti/peruuta',
    jobAlertsConfirm: 'tyokalut/tyopaikkavahti/vahvista',
    searchesAndAlerts: 'tyokalut/haut-ja-halytykset',
    watchlist: 'tyokalut/seurattavat',
    partnerships: 'kumppanuudet',
    privacy: 'tietosuoja',
    terms: 'kayttoehdot',
    features: 'ominaisuudet',
    forEmployers: 'tyonantajille',
    locations: 'sijainnit',
    summerJobs: 'tyoelama',
    studentsAndGraduates: 'tyoelama',
    bestCompanies: 'tyonantajat',
    employersAz: 'aakkosittain',
    company: 'tyonantaja',
    // "ura" ("career") — short, matches how Finnish company sites name their careers pages
    // ("ura meillä", "avoimet työpaikat"); distinct from company → "yritys".
    careers: 'ura',
    freeCareersPage: 'ilmainen-urasivu',
    // "palkka" (singular "salary") matches the real query pattern people search — "{rooli} palkka" —
    // and the page's own content ("{role}: palkka Suomessa"), not "palkat"/"palkkavertailu" (plural/
    // comparison framing). Distinct from the retired /palkkavertailu tool (removed-salary-comparison-path.ts).
    salary: 'palkka',
    about: 'tietoa-jobcrawlsista',
    editorialStandards: 'toimitusperiaatteet',
    contact: 'yhteystiedot',
    sitemap: 'sivukartta',
    accessibilityStatement: 'saavutettavuusseloste',
    accountDeletion: 'tilinpoisto',
    aboutTeam: 'tietoa-jobcrawlsista/tiimi',
    recruiterContactInfo: 'rekrytoijien-yhteystiedot',
    // Singular "työehtosopimus" (~2,000 searches/mo) rather than the plural (~100/mo); per-agreement
    // pages read naturally under it too (/fi/tyoehtosopimus/kaupan-tyoehtosopimus). TEC-645.
    collectiveAgreements: 'tyoehtosopimus',
  },
  sv: {
    job: 'jobb',
    jobs: 'lediga-jobb',
    rolesIndex: 'yrken',
    search: 'sok', // sök without special characters
    blog: 'arbetsliv',
    profile: 'profil',
    savedJobs: 'verktyg/sparade-jobb',
    savedSearches: 'verktyg/sparade-sokningar', // sökningar without special characters
    appliedJobs: 'verktyg/sokt-jobb',
    viewedJobs: 'verktyg/visade-jobb',
    jobFlowSankey: 'verktyg/jobbflode-sankey',
    jobAlertsUnsubscribe: 'verktyg/jobbevakning/avsluta',
    jobAlertsConfirm: 'verktyg/jobbevakning/bekrafta',
    searchesAndAlerts: 'verktyg/sokningar-och-bevakningar',
    watchlist: 'verktyg/bevakade',
    partnerships: 'partnerskap',
    privacy: 'integritet',
    terms: 'villkor',
    features: 'funktioner',
    forEmployers: 'for-arbetsgivare',
    locations: 'platser',
    summerJobs: 'arbetsliv',
    studentsAndGraduates: 'arbetsliv',
    bestCompanies: 'alla-arbetsgivare',
    employersAz: 'a-o',
    // Distinct from `business` → "foretag" (same word would collide URLs)
    company: 'arbetsgivare',
    // "karriär" with the diacritic stripped, per sv segment convention (sok, lon, for-arbetsgivare).
    careers: 'karriar',
    freeCareersPage: 'gratis-karriarsida',
    // "lön" with the diacritic stripped, matching every other sv segment's convention (sok, bekrafta,
    // for-arbetsgivare). Distinct from the retired /lonerjamforelse tool (removed-salary-comparison-path.ts).
    salary: 'lon',
    about: 'om-jobcrawls',
    editorialStandards: 'redaktionella-riktlinjer',
    contact: 'kontakt',
    sitemap: 'webbplatskarta',
    accessibilityStatement: 'tillganglighetsutlatande',
    accountDeletion: 'kontoradering',
    aboutTeam: 'om-jobcrawls/team',
    recruiterContactInfo: 'rekryterares-kontaktuppgifter',
    collectiveAgreements: 'kollektivavtal',
  },
} as const;

/**
 * The jobs-LISTING path segment per locale (e.g. 'tyopaikat', 'lediga-jobb') — just
 * `LOCALIZED_PATHS[locale].jobs`, exposed here so callers don't hand-roll their own
 * `locale === 'en' ? 'jobs' : locale === 'fi' ? 'tyopaikat' : '...'` mapping.
 *
 * Do not reach for 'jobb' when writing this by hand: it's sv's job-DETAIL segment
 * (LOCALIZED_PATHS.sv.job, /sv/jobb/{jobId}/{companyId}/{seoSlug}), not the jobs-LISTING
 * segment ('lediga-jobb'). Conflating the two — because 'jobb' also happens to be the plain
 * Swedish word for "jobs" — caused a real production bug (2026-09-02) where every sv job
 * page 404'd, independently duplicated across 8 files that each hand-rolled this mapping.
 * Import from here instead.
 */
export const JOBS_LISTING_SEGMENT: Record<keyof typeof LOCALIZED_PATHS, string> = {
  en: LOCALIZED_PATHS.en.jobs,
  fi: LOCALIZED_PATHS.fi.jobs,
  sv: LOCALIZED_PATHS.sv.jobs,
};

/**
 * All three locales' jobs-LISTING segments as a flat list, for matching "is this path segment
 * a jobs-listing segment in some locale" without knowing which locale up front (e.g. building
 * a `(jobs|tyopaikat|lediga-jobb)` regex alternation, or a `Set` for membership checks).
 */
export const JOBS_LISTING_SEGMENTS: readonly string[] = Object.values(JOBS_LISTING_SEGMENT);

/**
 * Employer-profile path segments that used to be current (`/en/company/{id}`, `/fi/yritys/{id}`).
 * They still route to the profile page, which 301s them to `LOCALIZED_PATHS[locale].company`
 * with the brand slug in one hop (see pages/company/@companyslug/+data.server.ts). Swedish never
 * changed (`arbetsgivare`).
 */
export const COMPANY_PROFILE_LEGACY_SEGMENTS: Record<keyof typeof LOCALIZED_PATHS, readonly string[]> = {
  en: ['company'],
  fi: ['yritys'],
  sv: [],
};

/** Retired employers-index segments ("best companies" ranking hub); url-rewrite-config 301s them. */
export const BEST_COMPANIES_LEGACY_SEGMENTS: Record<keyof typeof LOCALIZED_PATHS, readonly string[]> = {
  en: ['best-companies'],
  fi: ['parhaat-yritykset'],
  sv: ['basta-foretag'],
};

// Reverse mapping from English paths to LOCALIZED_PATHS keys
export const ENGLISH_PATH_TO_KEY: Record<string, keyof typeof LOCALIZED_PATHS.en> = {
  '/': 'search', // home/search page
  '/job': 'job',
  '/jobs': 'jobs', 
  '/search': 'search',
  '/blog': 'blog',
  '/resources': 'blog',
  '/profile': 'profile',
  '/tools/saved-jobs': 'savedJobs',
  '/tools/saved-searches': 'savedSearches',
  '/tools/applied-jobs': 'appliedJobs',
  '/tools/viewed-jobs': 'viewedJobs',
  '/tools/job-flow-sankey': 'jobFlowSankey',
  '/tools/job-alerts/unsubscribe': 'jobAlertsUnsubscribe',
  '/tools/job-alerts/confirm': 'jobAlertsConfirm',
  '/tools/searches': 'searchesAndAlerts',
  '/tools/watchlist': 'watchlist',
  '/partnerships': 'partnerships',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/features': 'features',
  '/for-employers': 'forEmployers',
  '/free-careers-page': 'freeCareersPage',
  '/locations': 'locations',
  '/resources/summer-jobs': 'summerJobs',
  '/resources/students-and-graduates': 'studentsAndGraduates',
  '/best-companies': 'bestCompanies',
  '/about': 'about',
  '/editorial-standards': 'editorialStandards',
  '/contact': 'contact',
  '/sitemap': 'sitemap',
  '/accessibility-statement': 'accessibilityStatement',
  '/account-deletion': 'accountDeletion',
  '/about/team': 'aboutTeam',
  '/recruiter-contact-info': 'recruiterContactInfo',
  '/collective-agreements': 'collectiveAgreements',
} as const;

/**
 * Converts an English route path to a localized path for the given locale
 * @param englishPath - The English path (e.g., "/saved-jobs")
 * @param locale - Target locale ('en', 'fi', 'sv')
 * @returns Localized path (e.g., "/tallennetut-tyopaikat" for Finnish)
 */
export function localizeRoute(englishPath: string, locale: keyof typeof LOCALIZED_PATHS): string {
  // A query or an anchor rides along: localize the path part only ("/tools/searches#scout").
  const suffixAt = englishPath.search(/[?#]/);
  if (suffixAt > 0) return `${localizeRoute(englishPath.slice(0, suffixAt), locale)}${englishPath.slice(suffixAt)}`;
  // Handle root path specially
  if (englishPath === '/') {
    return '/';
  }
  
  // Handle resource hub and nested resource paths (alias of blog)
  if (englishPath === '/resources' || englishPath.startsWith('/resources/')) {
    const remainder = englishPath.replace(/^\/resources\/?/, '');
    const localizedBlog = LOCALIZED_PATHS[locale].blog;
    if (!remainder) {
      return `/${localizedBlog}`;
    }
    // Summer jobs uses a different slug per locale (not English "summer-jobs" under /tyoelama).
    if (remainder === 'summer-jobs' || remainder.startsWith('summer-jobs/')) {
      const base = localizedSummerJobsArticlePath(locale as 'en' | 'fi' | 'sv');
      if (remainder === 'summer-jobs') {
        return base;
      }
      const sub = remainder.slice('summer-jobs'.length).replace(/^\//, '');
      return sub ? `${base}/${sub}` : base;
    }
    return `/${localizedBlog}/${remainder}`;
  }

  // Employers index + city pages (logical path: /best-companies, /best-companies/:slug, which is
  // Vike's filesystem route; public segment is LOCALIZED_PATHS[locale].bestCompanies)
  if (englishPath === '/best-companies' || englishPath.startsWith('/best-companies/')) {
    const locSeg = LOCALIZED_PATHS[locale].bestCompanies;
    if (englishPath === '/best-companies') {
      return `/${locSeg}`;
    }
    const sub = englishPath.slice('/best-companies/'.length);
    // A to Z directory: logical /best-companies/a-z[/{letter}] -> localized segment (aakkosittain, a-o)
    const azSeg = LOCALIZED_PATHS[locale].employersAz;
    if (sub === 'a-z' || sub.startsWith('a-z/')) {
      return `/${locSeg}/${azSeg}${sub.slice('a-z'.length)}`;
    }
    return `/${locSeg}/${sub}`;
  }

  // Employer profile (logical: /company/:companyslug, Vike's filesystem route; public segment
  // is LOCALIZED_PATHS[locale].company, e.g. /en/employer/, /fi/tyonantaja/)
  if (englishPath.startsWith('/company/')) {
    const locSeg = LOCALIZED_PATHS[locale].company;
    const rest = englishPath.slice('/company/'.length);
    return `/${locSeg}/${rest}`;
  }

  // Hosted careers pages (logical: /careers/:companyslug — TEC-488)
  const caEn = LOCALIZED_PATHS.en.careers;
  if (englishPath.startsWith(`/${caEn}/`)) {
    const locSeg = LOCALIZED_PATHS[locale].careers;
    const rest = englishPath.slice(`/${caEn}/`.length);
    return `/${locSeg}/${rest}`;
  }

  // Collective agreements directory + per-agreement pages (logical: /collective-agreements[/:slug])
  const caDirEn = LOCALIZED_PATHS.en.collectiveAgreements;
  if (englishPath === `/${caDirEn}` || englishPath.startsWith(`/${caDirEn}/`)) {
    return `/${LOCALIZED_PATHS[locale].collectiveAgreements}${englishPath.slice(caDirEn.length + 1)}`;
  }

  // Salary guide hub + per-role pages (logical path: /salary, /salary/:role)
  const saEn = LOCALIZED_PATHS.en.salary;
  if (englishPath === `/${saEn}` || englishPath.startsWith(`/${saEn}/`)) {
    const locSeg = LOCALIZED_PATHS[locale].salary;
    if (englishPath === `/${saEn}`) {
      return `/${locSeg}`;
    }
    const sub = englishPath.slice(`/${saEn}/`.length);
    return `/${locSeg}/${sub}`;
  }

  // Backward-compat for old /blog nested paths
  if (englishPath.startsWith('/blog/')) {
    const remainder = englishPath.replace(/^\/blog\/?/, '');
    const localizedBlog = LOCALIZED_PATHS[locale].blog;
    if (!remainder) {
      return `/${localizedBlog}`;
    }
    if (remainder === 'summer-jobs' || remainder.startsWith('summer-jobs/')) {
      const base = localizedSummerJobsArticlePath(locale as 'en' | 'fi' | 'sv');
      if (remainder === 'summer-jobs') {
        return base;
      }
      const sub = remainder.slice('summer-jobs'.length).replace(/^\//, '');
      return sub ? `${base}/${sub}` : base;
    }
    return `/${localizedBlog}/${remainder}`;
  }

  // Handle location-based paths (e.g., /jobs/location/Helsinki_Finland)
  if (englishPath.startsWith('/jobs/location/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const locationPart = englishPath.replace('/jobs/location/', '');
    // Use the correct location segment based on locale
    const locationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
    // Localize the location part (e.g., "Helsinki_Finland" -> "Helsinki_Suomi" for Finnish)
    const localizedLocationPart = encodeLocationUrlPathSegment(localizeLocationPart(locationPart, locale));
    return `/${localizedJobs}/${locationSegment}/${localizedLocationPart}`;
  }

  // Handle company-based paths (e.g., /jobs/company/company-name)
  if (englishPath.startsWith('/jobs/company/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const companyPart = englishPath.replace('/jobs/company/', '');
    return `/${localizedJobs}/company/${companyPart}`;
  }

  // Work-model hubs (logical /jobs/remote[/...], /jobs/hybrid[/...]): localize the jobs segment
  // and the work-model segment, keep any tail (alue/…, ammatti/…) as-is; the SRP matcher accepts
  // localized markers. The retired scope form (/jobs/remote/Finland) maps to the plain hub,
  // which is also where url-rewrite-config 301s it.
  {
    const wmMatch = englishPath.match(/^\/jobs\/(remote|hybrid)(?:\/(.*))?$/);
    if (wmMatch) {
      const key = wmMatch[1] as WorkModelKey;
      const tail = (wmMatch[2] ?? '').replace(/\/$/, '');
      const isRetiredScope = key === 'remote' && (tail === 'Finland' || tail === 'Europe');
      const base = `/${LOCALIZED_PATHS[locale].jobs}/${workModelSegment(key, locale)}`;
      return tail && !isRetiredScope ? `${base}/${tail}` : base;
    }
  }

  // Roles index (logical /jobs/roles): the index segment is localized, unlike role slugs.
  if (englishPath === '/jobs/roles') {
    return `/${LOCALIZED_PATHS[locale].jobs}/${LOCALIZED_PATHS[locale].rolesIndex}`;
  }

  // Handle other dynamic job paths (e.g., /jobs/role/developer)
  if (englishPath.startsWith('/jobs/')) {
    const localizedJobs = LOCALIZED_PATHS[locale].jobs;
    const jobPart = englishPath.replace('/jobs/', '');
    return `/${localizedJobs}/${jobPart}`;
  }

  // Remove leading slash for lookup
  const pathKey = ENGLISH_PATH_TO_KEY[englishPath];

  if (!pathKey) {
    // Extra tool pages (e.g. interviewing-jobs) and the /tools hub root itself share
    // /tools[/<tail>] in English filesystem routes; map tools → tyokalut|verktyg for fi/sv
    // while keeping any tail segment.
    if (englishPath === '/tools' || englishPath.startsWith('/tools/')) {
      const remainder = englishPath.replace(/^\/tools\/?/, '');
      if (locale === 'en') {
        return remainder ? `/tools/${remainder}` : '/tools';
      }
      const toolsRoot = locale === 'fi' ? 'tyokalut' : 'verktyg';
      return remainder ? `/${toolsRoot}/${remainder}` : `/${toolsRoot}`;
    }
    return englishPath;
  }

  const localizedSegment = LOCALIZED_PATHS[locale][pathKey];
  return `/${localizedSegment}`;
}

// Build reverse mapping from localized paths to English paths
const LOCALIZED_PATH_TO_ENGLISH: Record<string, Record<string, string>> = {};

Object.keys(LOCALIZED_PATHS).forEach(locale => {
  if (locale === 'en') return; // Skip English as it's the base
  
  LOCALIZED_PATH_TO_ENGLISH[locale] = {};
  
  Object.entries(LOCALIZED_PATHS[locale as keyof typeof LOCALIZED_PATHS]).forEach(([key, localizedPath]) => {
    const englishPath = LOCALIZED_PATHS.en[key as keyof typeof LOCALIZED_PATHS.en];
    LOCALIZED_PATH_TO_ENGLISH[locale][localizedPath] = englishPath;
  });
});

/**
 * Converts a localized path back to its English equivalent
 * @param localizedPath - The localized path (e.g., "/tallennetut-tyopaikat")
 * @param locale - The locale of the path ('fi', 'sv')
 * @returns The English equivalent path (e.g., "/saved-jobs")
 */
export function delocalizeRoute(localizedPath: string, locale: keyof typeof LOCALIZED_PATHS): string {
  // English mostly equals the logical route names, with two exceptions since 2026-09-04: the
  // employers index (/employers > logical /best-companies) and employer profiles
  // (/employer/{slug} > logical /company/{slug}). Map those, then return everything else as-is.
  if (locale === 'en') {
    for (const seg of [LOCALIZED_PATHS.en.bestCompanies, ...BEST_COMPANIES_LEGACY_SEGMENTS.en]) {
      if (localizedPath === `/${seg}`) return '/best-companies';
      if (localizedPath.startsWith(`/${seg}/`)) return `/best-companies/${localizedPath.slice(`/${seg}/`.length)}`;
    }
    const enCompany = LOCALIZED_PATHS.en.company;
    if (localizedPath.startsWith(`/${enCompany}/`)) {
      return `/company/${localizedPath.slice(`/${enCompany}/`.length)}`;
    }
    return localizedPath;
  }

  // Handle root path
  if (localizedPath === '/' || localizedPath === '') {
    return '/';
  }

  // Employers index: localized segment + optional city slug. Retired segments
  // (parhaat-yritykset, basta-foretag) still map so their 301 rules and the page agree.
  for (const seg of [LOCALIZED_PATHS[locale].bestCompanies, ...BEST_COMPANIES_LEGACY_SEGMENTS[locale]]) {
    if (localizedPath === `/${seg}` || localizedPath.startsWith(`/${seg}/`)) {
      if (localizedPath === `/${seg}`) return '/best-companies';
      const sub = localizedPath.slice(`/${seg}/`.length);
      const azSeg = LOCALIZED_PATHS[locale].employersAz;
      if (sub === azSeg || sub.startsWith(`${azSeg}/`)) return `/best-companies/a-z${sub.slice(azSeg.length)}`;
      return `/best-companies/${sub}`;
    }
  }
  // Legacy: English segment under fi/sv (before redirects)
  if (localizedPath === '/best-companies' || localizedPath.startsWith('/best-companies/')) {
    return localizedPath;
  }

  // Employer profile: /tyonantaja/slug or /arbetsgivare/slug -> /company/slug (the logical
  // English path keeps the `company` name: it is Vike's filesystem route, not a public URL).
  // Legacy segments (/yritys/, /company/) still map here so the page can 301 them itself.
  for (const seg of [LOCALIZED_PATHS[locale].company, ...COMPANY_PROFILE_LEGACY_SEGMENTS[locale]]) {
    if (localizedPath.startsWith(`/${seg}/`)) {
      return `/company/${localizedPath.slice(`/${seg}/`.length)}`;
    }
  }
  if (localizedPath.startsWith('/company/')) {
    return localizedPath;
  }

  // Hosted careers pages: /ura/slug or /karriar/slug -> /careers/slug (TEC-488)
  const locCa = LOCALIZED_PATHS[locale].careers;
  if (localizedPath.startsWith(`/${locCa}/`)) {
    return `/careers/${localizedPath.slice(`/${locCa}/`.length)}`;
  }
  if (localizedPath.startsWith('/careers/')) {
    return localizedPath;
  }

  // Collective agreements: /tyoehtosopimus[/slug], /kollektivavtal[/slug] -> /collective-agreements[/slug].
  // The English segment under fi/sv (pre-TEC-645 URLs) passes through; url-rewrite-config 301s it.
  const locCaDir = LOCALIZED_PATHS[locale].collectiveAgreements;
  if (localizedPath === `/${locCaDir}` || localizedPath.startsWith(`/${locCaDir}/`)) {
    return `/${LOCALIZED_PATHS.en.collectiveAgreements}${localizedPath.slice(locCaDir.length + 1)}`;
  }

  // Salary guide: localized segment + optional role slug
  const locSa = LOCALIZED_PATHS[locale].salary;
  if (localizedPath === `/${locSa}` || localizedPath.startsWith(`/${locSa}/`)) {
    return localizedPath === `/${locSa}`
      ? '/salary'
      : `/salary/${localizedPath.slice(`/${locSa}/`.length)}`;
  }
  // Legacy: URLs indexed/bookmarked before this locale segment existed used the English word
  // ("/fi/salary/…", "/sv/salary/…") — keep resolving them so nothing 404s during the transition;
  // canonical/hreflang/sitemap generation always emits the new localized form going forward.
  if (localizedPath === '/salary' || localizedPath.startsWith('/salary/')) {
    return localizedPath;
  }

  // Roles index: /tyopaikat/ammatit, /lediga-jobb/yrken (and the English form) -> /jobs/roles
  if (localizedPath === `/${LOCALIZED_PATHS[locale].jobs}/${LOCALIZED_PATHS[locale].rolesIndex}` || localizedPath === '/jobs/roles') {
    return '/jobs/roles';
  }
  // Handle location-based paths (e.g., /tyopaikat/alue/Helsinki_Suomi -> /jobs/location/Helsinki_Finland)
  const localizedJobs = LOCALIZED_PATHS[locale].jobs;
  const locationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
  // Only the first segment is the location; anything after it (`/ammatti/{role}`,
  // `/tyonantaja/{id}`, `/kategoria/{slug}`) is kept verbatim, since match-search-index-path.ts
  // accepts the localized markers. Folding the whole tail into delocalizeLocationPart() used to
  // yield `/jobs/location/tampere_suomiammattisairaanhoitaja`, which lost the role route param
  // and forced every role x city page to noindex.
  const delocalizeLocationPath = (locationPath: string): string => {
    const [locationPart, ...rest] = locationPath.split('/');
    const tail = rest.filter(Boolean).join('/');
    return `/jobs/location/${delocalizeLocationPart(locationPart)}${tail ? `/${tail}` : ''}`;
  };
  if (localizedPath.startsWith(`/${localizedJobs}/${locationSegment}/`)) {
    return delocalizeLocationPath(localizedPath.replace(`/${localizedJobs}/${locationSegment}/`, ''));
  }
  // Also handle English 'location' segment in case it appears in a non-English locale URL
  if (localizedPath.startsWith(`/${localizedJobs}/location/`)) {
    return delocalizeLocationPath(localizedPath.replace(`/${localizedJobs}/location/`, ''));
  }

  // Handle company-based paths (e.g., /tyopaikat/company/company-name -> /jobs/company/company-name)
  if (localizedPath.startsWith(`/${localizedJobs}/company/`)) {
    const companyPart = localizedPath.replace(`/${localizedJobs}/company/`, '');
    return `/jobs/company/${companyPart}`;
  }

  // Remote hubs + other dynamic job paths (e.g. /tyopaikat/etätyö/Suomi, /tyopaikat/role/developer)
  if (localizedPath.startsWith(`/${localizedJobs}/`)) {
    const rest = localizedPath.slice(`/${localizedJobs}/`.length);
    const parts = rest.split('/').filter(Boolean);
    // Work-model hubs: /tyopaikat/etatyo[/…] -> /jobs/remote[/…] (tail kept verbatim).
    const wmKey = workModelForSegment(parts[0]);
    if (wmKey && !(parts.length >= 2 && remoteScopeSlugToEnglishScope(parts[1]))) {
      // A city after the work model is delocalized like a plain location page
      // (alue/helsinki_suomi -> location/helsinki_finland) so the SRP loader recognises the
      // curated hub; any category/role markers after it are kept verbatim.
      const locMarkers = new Set(['alue', 'omrade', 'område', 'location']);
      let tailParts = parts.slice(1);
      if (tailParts.length >= 2 && locMarkers.has(tailParts[0].toLowerCase())) {
        tailParts = ['location', delocalizeLocationPart(tailParts[1]), ...tailParts.slice(2)];
      }
      const tail = tailParts.join('/');
      return tail ? `/jobs/${wmKey}/${tail}` : `/jobs/${wmKey}`;
    }
    if (parts.length >= 2 && isRemotePathMarker(parts[0])) {
      let scopeRaw = parts[1];
      try {
        scopeRaw = decodeURIComponent(parts[1]);
      } catch {
        /* ignore */
      }
      const scope = remoteScopeSlugToEnglishScope(scopeRaw);
      if (scope) {
        const enFacet = remoteHubScopeToLocationFacet('en', scope);
        return `/jobs/location/${toLocationSlug(enFacet)}`;
      }
    }
    const jobPart = localizedPath.replace(`/${localizedJobs}/`, '');
    return `/jobs/${jobPart}`;
  }
  
  // Remove leading slash for lookup
  const pathKey = localizedPath.startsWith('/') ? localizedPath.slice(1) : localizedPath;
  
  // Look up the English equivalent
  if (LOCALIZED_PATH_TO_ENGLISH[locale] && LOCALIZED_PATH_TO_ENGLISH[locale][pathKey]) {
    return `/${LOCALIZED_PATH_TO_ENGLISH[locale][pathKey]}`;
  }

  // Unmapped verktyg/tyokalut[/*] (e.g. bare hub root, or a tail like interviewing-jobs) →
  // /tools[/*] logical path.
  if (locale === 'sv' && (localizedPath === '/verktyg' || localizedPath.startsWith('/verktyg/'))) {
    const rest = localizedPath.slice('/verktyg'.length).replace(/^\//, '');
    return rest ? `/tools/${rest}` : '/tools';
  }
  if (locale === 'fi' && (localizedPath === '/tyokalut' || localizedPath.startsWith('/tyokalut/'))) {
    const rest = localizedPath.slice('/tyokalut'.length).replace(/^\//, '');
    return rest ? `/tools/${rest}` : '/tools';
  }

  // Fallback: return original path
  return localizedPath;
}

export const BASE_URL = 'https://www.jobcrawls.com';
/** @deprecated Prefer getDevSiteOrigin() for dev — port may differ from 3000. Kept for tests and legacy strips. */
export const DEV_URL = FALLBACK_DEV_SITE_ORIGIN;

/**
 * Public site origin, for assets on it (e.g. /site-media/... or /logo.svg) and for absolute
 * in-site links. In dev uses the current dev origin (browser) or request URL / AUTH_BASE_URL on SSR.
 * Never derive link origins from an API fetch base (INTERNAL_API_BASE_URL is the raw API Gateway host).
 */
export function getPublicAssetBaseUrl(requestUrl?: string | null): string {
  return VITE_DEV ? getDevSiteOrigin({ requestUrl }) : BASE_URL;
}

/** Remote / Distans scope labels (not city names) — kept separate from {@link FINNISH_LOCALITY_LOCALE_NAMES}. */
const REMOTE_SCOPE_LOCALE_NAMES: Record<string, { fi: string; sv: string; en: string }> = {
  Remote: { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  'Remote -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
  Etätyö: { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  Distans: { fi: 'Etätyö', sv: 'Distans', en: 'Remote' },
  'Etätyö -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
  'Distans -': { fi: 'Etätyö -', sv: 'Distans -', en: 'Remote -' },
};

/**
 * City name mappings across locales for URL generation (localities + remote scopes)
 */
const CITY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  ...FINNISH_LOCALITY_LOCALE_NAMES,
  ...REMOTE_SCOPE_LOCALE_NAMES,
};

/**
 * Country name mappings for different locales
 */
const COUNTRY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  'Finland': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Suomi': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Europe': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Europa': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Eurooppa': { fi: 'Eurooppa', sv: 'Europa', en: 'Europe' },
  'Global': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'Globalt': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'Globaali': { fi: 'Globaali', sv: 'Globalt', en: 'Global' },
  'MENA': { fi: 'MENA', sv: 'MENA', en: 'MENA' },
};

/** Case-insensitive lookup into a Title-Case-keyed dictionary (lowercase URL segments must still resolve). */
function lookupLocationNameCaseInsensitive<T>(dict: Record<string, T>, key: string): T | undefined {
  const lowerKey = key.toLowerCase();
  for (const k in dict) {
    if (k.toLowerCase() === lowerKey) return dict[k];
  }
  return undefined;
}

/**
 * Convert a localized location slug back to its canonical English form.
 * e.g. "Helsingfors_Finland" -> "Helsinki_Finland", "Helsinki_Suomi" -> "Helsinki_Finland"
 */
export function delocalizeLocationPart(locationPart: string): string {
  const parts = locationPart.split('_').filter(p => p !== '');
  if (parts.length === 0) return locationPart;

  const country = parts[parts.length - 1];
  const city = parts.slice(0, -1).join(' ');

  const englishCity = lookupLocationNameCaseInsensitive(CITY_NAME_MAPPINGS, city)?.en || city;
  const englishCountry = lookupLocationNameCaseInsensitive(COUNTRY_NAME_MAPPINGS, country)?.en || country;

  return toLocationSlug(`${englishCity} ${englishCountry}`.trim());
}

/**
 * Localize location part (e.g., "Helsinki_Finland" -> "Helsinki_Suomi" for Finnish)
 */
function localizeLocationPart(locationPart: string, locale: keyof typeof LOCALIZED_PATHS): string {
  const parts = locationPart.split('_');
  if (parts.length === 0) return locationPart;

  // Defensive: drop empty tokens (e.g. from a stale "_-_"-style slug still in circulation).
  const cleanParts = parts.filter(p => p !== '');

  // Last part is country, everything before is city
  const country = cleanParts[cleanParts.length - 1];
  const city = cleanParts.slice(0, -1).join(' ');

  const mappedCity = lookupLocationNameCaseInsensitive(CITY_NAME_MAPPINGS, city)?.[locale] || city;
  const mappedCountry = lookupLocationNameCaseInsensitive(COUNTRY_NAME_MAPPINGS, country)?.[locale] || country;

  return toLocationSlug(`${mappedCity} ${mappedCountry}`.trim());
}
